import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Clock,
  Target,
  Trophy,
  Sparkles,
  ShoppingCart,
  ArrowLeft,
  Zap,
  BarChart3,
  Award,
  MessageSquare,
  Infinity,
  Shield,
  Loader2
} from 'lucide-react';

const Upgrade = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch courses from API
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(`${API}/categories`);
        if (response.ok) {
          const data = await response.json();
          const categoriesArray = data.categories || data;
          // Filter to only available courses and sort by group
          const availableCourses = categoriesArray.filter(c => c.is_available !== false);
          setCourses(availableCourses);
        }
      } catch (error) {
        console.error('Failed to fetch courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // Group courses by their group (PMI, Scrum, etc.)
  const groupedCourses = courses.reduce((acc, course) => {
    const group = course.group || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(course);
    return acc;
  }, {});

  // Calculate total questions across all courses
  const totalQuestions = courses.reduce((sum, c) => sum + (c.questions || 0), 0);

  const benefits = [
    {
      icon: Infinity,
      title: 'Unlimited Questions',
      description: 'Practice as much as you want - no limits'
    },
    {
      icon: Clock,
      title: 'Realistic Exam Simulation',
      description: 'Test yourself under real exam conditions'
    },
    {
      icon: BarChart3,
      title: 'Detailed Progress Tracking',
      description: 'Track your improvement over time'
    },
    {
      icon: Award,
      title: 'Badges & Gamification',
      description: 'Collect badges and maintain your streak'
    },
    {
      icon: MessageSquare,
      title: 'AI Learning Assistant',
      description: 'Get instant explanations for all questions'
    },
    {
      icon: Shield,
      title: '6 Months Full Access',
      description: 'Plenty of time for your exam preparation'
    }
  ];

  // Dynamic packages based on courses
  const generatePackages = () => {
    const pmiCourses = courses.filter(c => c.group === 'PMI');
    const scrumCourses = courses.filter(c => c.group === 'Scrum');
    
    const singlePrice = courses.length > 0 ? Math.min(...courses.map(c => c.price || 29)) : 29;
    const comboPrice = singlePrice * 2 - 9; // €9 discount for combo
    
    return [
      {
        name: 'Single Course',
        price: singlePrice.toString(),
        description: 'One certification course of your choice',
        features: [
          courses.map(c => c.id || c.name).slice(0, 4).join(', ') + (courses.length > 4 ? '...' : ''),
          'All practice questions',
          'Exam simulation',
          '6 months access'
        ],
        popular: false
      },
      {
        name: 'Combo Package',
        price: comboPrice.toString(),
        originalPrice: (singlePrice * 2).toString(),
        description: '2 courses of your choice',
        features: [
          'Any 2 certifications',
          'Both exam simulations',
          'Combined progress tracking',
          'Save €9'
        ],
        popular: true
      }
    ];
  };

  const packages = generatePackages();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-slate-300 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          {user?.is_guest && (
            <Badge variant="outline" className="border-orange-500 text-orange-400">
              Guest Mode
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 text-sm font-medium">Unlock Premium Features</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Ready for Your Certification?
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Unlock all features and prepare optimally for your exam.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {benefits.map((benefit) => (
            <Card key={benefit.title} className="bg-slate-800/50 border-slate-700/50 hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400 text-sm">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Available Courses Section */}
        {loading ? (
          <div className="flex items-center justify-center py-12 mb-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        ) : courses.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white text-center mb-8">Available Certifications</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {courses.map((course) => (
                <Card 
                  key={course.id} 
                  className="bg-slate-800/50 border-slate-700/50 hover:border-blue-500/30 transition-all"
                >
                  <CardContent className="p-4 text-center">
                    <div 
                      className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center text-lg"
                      style={{ backgroundColor: course.color || '#0047AB' }}
                    >
                      <span className="text-white">{course.icon || '📚'}</span>
                    </div>
                    <p className="font-semibold text-white text-sm">{course.id || course.name}</p>
                    <p className="text-xs text-slate-400 mt-1">{course.questions} Questions</p>
                    <p className="text-blue-400 font-bold mt-2">€{course.price || 29}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
          {packages.map((pkg) => (
            <Card 
              key={pkg.name} 
              className={`relative overflow-hidden ${
                pkg.popular 
                  ? 'bg-gradient-to-br from-blue-600 to-blue-700 border-blue-500' 
                  : 'bg-slate-800/50 border-slate-700/50'
              }`}
            >
              {pkg.popular && (
                <div className="absolute top-4 right-4">
                  <Badge className="bg-amber-400 text-amber-900 font-bold">
                    <Zap className="w-3 h-3 mr-1" />
                    BELIEBT
                  </Badge>
                </div>
              )}
              <CardContent className="p-8">
                <h3 className={`text-xl font-bold mb-2 ${pkg.popular ? 'text-white' : 'text-white'}`}>
                  {pkg.name}
                </h3>
                <p className={`text-sm mb-4 ${pkg.popular ? 'text-blue-100' : 'text-slate-400'}`}>
                  {pkg.description}
                </p>
                
                <div className="flex items-baseline gap-2 mb-6">
                  <span className={`text-4xl font-bold ${pkg.popular ? 'text-white' : 'text-white'}`}>
                    €{pkg.price}
                  </span>
                  {pkg.originalPrice && (
                    <span className="text-blue-200 line-through text-lg">€{pkg.originalPrice}</span>
                  )}
                  <span className={`text-sm ${pkg.popular ? 'text-blue-100' : 'text-slate-400'}`}>
                    / 6 months
                  </span>
                </div>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature) => (
                    <li key={`${pkg.name}-${feature}`} className="flex items-center gap-3">
                      <CheckCircle className={`w-5 h-5 ${pkg.popular ? 'text-blue-200' : 'text-green-400'}`} />
                      <span className={pkg.popular ? 'text-white' : 'text-slate-300'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => navigate('/store')}
                  className={`w-full h-12 font-semibold ${
                    pkg.popular 
                      ? 'bg-white text-blue-600 hover:bg-blue-50' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                  data-testid={`upgrade-${pkg.name.toLowerCase().replace(' ', '-')}-btn`}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Buy Now
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Section */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-8 mb-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">{totalQuestions}+</p>
              <p className="text-sm text-slate-400">Practice Questions</p>
            </div>
            <div className="w-px h-12 bg-slate-700" />
            <div className="text-center">
              <p className="text-3xl font-bold text-white">{courses.length}</p>
              <p className="text-sm text-slate-400">Certifications</p>
            </div>
            <div className="w-px h-12 bg-slate-700" />
            <div className="text-center">
              <p className="text-3xl font-bold text-white">6</p>
              <p className="text-sm text-slate-400">Months Access</p>
            </div>
          </div>
          
          <p className="text-slate-400 mb-6">
            Secure payment via Stripe • Instant access after purchase
          </p>

          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Continue as Guest
            </Button>
            <Button
              onClick={() => navigate('/store')}
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Go to Store
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Upgrade;
